import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity,
  Boxes,
  ClipboardList,
  FileBarChart,
  History,
  LayoutDashboard,
  LogOut,
  Map,
  Settings,
  ShieldAlert,
} from "lucide-react";

import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth, hasPermission } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface NavLinkSpec {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  permission?: string;
}

const NAV: NavLinkSpec[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/fleet", label: "Flota", icon: Map, permission: "units.read" },
  { to: "/operativos", label: "Operativos", icon: ClipboardList, permission: "operativos.view.tenant" },
  { to: "/hits", label: "Hits", icon: ShieldAlert, permission: "hits.view.tenant" },
  { to: "/hotlist", label: "Hotlist", icon: Boxes, permission: "hotlist.read" },
  { to: "/catalogos", label: "Catálogos", icon: Activity },
  { to: "/reports", label: "Reportes", icon: FileBarChart, permission: "reports.download" },
  { to: "/audit", label: "Auditoría", icon: History, permission: "audit.read.tenant" },
  { to: "/settings", label: "Configuración", icon: Settings },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="flex h-screen bg-bg">
      <aside className="w-60 shrink-0 border-r border-border bg-surface flex flex-col">
        <div className="px-4 py-5 border-b border-border">
          <div className="flex items-center gap-2">
            <ShieldAlert className="text-primary-400" size={20} />
            <div>
              <div className="text-sm font-semibold">ARGOS</div>
              <div className="text-xs text-muted-200">{user?.tenantSlug}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
          {NAV.filter((n) => !n.permission || hasPermission(user, n.permission)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
                  isActive
                    ? "bg-primary/15 text-primary-400"
                    : "text-foreground hover:bg-surface2"
                )
              }
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-border space-y-2">
          <div className="text-xs text-muted-200">{user?.nombre}</div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-200">Tema</span>
            <ThemeToggle />
          </div>
          <button
            type="button"
            data-testid="logout-button"
            className="btn-ghost w-full justify-start"
            onClick={async () => {
              await logout();
              nav("/login", { replace: true });
            }}
          >
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
